import styled from '@emotion/styled';
import { BreakpointsEnum } from '../../helpers/breakpoints';
import * as React from 'react';
import { useState, useRef } from 'react';
import { TextCollapsible } from '../TextCollapsible/TextCollapsible';
import { __ } from '../../helpers/TranslationService';
import { ISpecials } from '../../interfaces';

interface IProps {
    specials: ISpecials[];
    height?: number;
}

const SpecialsWrapper = styled.div`
    max-width: 1150px;
    margin: 0 auto;
`;

const SpecialsHeader = styled.div`
    font-size: 16px;
    color: ${({ theme }) => theme.colors.white[100]};
    display: flex;
    align-items: center;
    background-color: ${({ theme }) => theme.productPage.specialsHeaderBackground};
    width: 100%;
    height: 45px;
    justify-content: left;
    padding-left: 25px;

    @media (min-width: ${BreakpointsEnum.md}) {
        width: 100%;
        height: unset;
        font-size: 16px;
        font-weight: bold;
        padding: 12px 24px;
        margin-top: 25px;
        display: inline-block;
        border-radius: 6px 6px 0 0;
    }
`;

const SpecialsContent = styled.div`
    padding: 7px 25px;

    @media (min-width: ${BreakpointsEnum.md}) {
        padding: 0;
        background: ${({ theme }) => theme.background.layer};
        border: 2px solid ${({ theme }) => theme.colors.seashell[100]};
        border-radius: 0 0 6px 6px;
    }
`;

const SpecialsItemStyled = styled.div`
    margin: 15px 0;

    strong,
    b {
        font-size: 16px;

        @media (min-width: ${BreakpointsEnum.md}) {
            font-weight: 700;
            line-height: 37px;
        }
    }

    .specials-description,
    p {
        margin: 5px 0;
        overflow: hidden;

        @media (min-width: ${BreakpointsEnum.md}) {
            margin: 0 0 8.5px;
        }
    }

    .specials-description,
    p,
    div {
        font-size: 13px;
        line-height: 1.46;

        @media (min-width: ${BreakpointsEnum.md}) {
            font-size: 14px;
            line-height: 20px;
        }

        strong,
        b {
            font-size: inherit;
            line-height: inherit;
        }

        a {
            color: ${({ theme }) => theme.link.primary};
        }
    }

    @media (min-width: ${BreakpointsEnum.md}) {
        margin: unset;
        padding: 8px 25px 11px 25px;
        border-bottom: 2px solid ${({ theme }) => theme.colors.whiteSmoke[100]};
        border-radius: 6px;

        &:last-of-type {
            border-bottom: unset;
        }

        ul {
            padding-inline-start: 40px;

            li {
                list-style-type: disc;
            }
        }
    }
`;

const SpecialsItem = (props) => {
    const elementRef = useRef(null);
    const [isOpen, toogleState] = useState(false);

    const toogleStateOpen = (isOpen) => {
        toogleState(isOpen);
    };

    const shouldShowFullHeight =
        props.height >= elementRef?.current?.scrollHeight - 5 || props.isDreamDeal;

    const height = !isOpen
        ? shouldShowFullHeight
            ? elementRef?.current?.scrollHeight
            : props.height
        : 'auto';

    return (
        <SpecialsItemStyled>
            {/* Content managers send html 🙄 */}
            <strong dangerouslySetInnerHTML={{ __html: props.title }} />
            <div
                ref={elementRef}
                className="specials-description"
                style={{ height }}
                dangerouslySetInnerHTML={{ __html: props.description_html }}
            />
            {!props.isDreamDeal && (isOpen || elementRef?.current?.scrollHeight > height) && (
                <TextCollapsible isOpen={isOpen} toogleStateOpen={toogleStateOpen} />
            )}
        </SpecialsItemStyled>
    );
};

export function Specials({ specials, height = 55 }: IProps) {
    return (
        <SpecialsWrapper id="specials">
            <SpecialsHeader>{__('Specials', 'dreamlines')}</SpecialsHeader>
            <SpecialsContent>
                {specials.map((special, i) => (
                    <SpecialsItem key={i} {...special} height={height} />
                ))}
            </SpecialsContent>
        </SpecialsWrapper>
    );
}
